import React from "react";
import { BarChart3 } from "lucide-react";
import DashboardCard from "../components/DashboardCard";
import DashboardCardSkeleton from "../components/DashboardCardSkeleton";
import DashboardEmptyState from "../components/DashboardEmptyState";
import DashboardPeriodFilter from "../components/DashboardPeriodFilter";
import { PROPERTY_ANALYTICS_PERIOD_PRESETS } from "../constants/dashboardPeriods";
import PropertyIdFilter, { PropertyIdFilterOption } from "../components/PropertyIdFilter";
import {
  DashboardPeriodPreset,
  IPropertyAnalyticsData,
  OwnerPatrimonyRequestStatus,
} from "../types/ownerDashboard.types";
import { isPropertyAnalyticsUnavailable } from "../utils/normalizePropertyAnalyticsData";
import PropertyAnalyticsBarChart from "./PropertyAnalyticsBarChart";
import PropertyAnalyticsLineChart from "./PropertyAnalyticsLineChart";
import PropertyAnalyticsSummary from "./PropertyAnalyticsSummary";
import PropertyAnalyticsTable from "./PropertyAnalyticsTable";

interface PropertyAnalyticsProps {
  data: IPropertyAnalyticsData | null;
  status: OwnerPatrimonyRequestStatus;
  periodPreset: DashboardPeriodPreset;
  propertyId: number | null;
  propertyOptions: PropertyIdFilterOption[];
  smDown: boolean;
  lgDown?: boolean;
  onPeriodChange: (period: DashboardPeriodPreset) => void;
  onPropertyChange: (propertyId: number | null) => void;
  onRetry: () => Promise<void>;
}

export default function PropertyAnalytics({
  data,
  status,
  periodPreset,
  propertyId,
  propertyOptions,
  smDown,
  lgDown = false,
  onPeriodChange,
  onPropertyChange,
  onRetry,
}: PropertyAnalyticsProps) {
  const padding = smDown ? "0.875rem" : lgDown ? "1rem" : "1.125rem";
  const isLoading = status === "loading" && !data;
  const isError = status === "error";
  const showRanking = propertyId == null;

  if (isLoading) {
    return <DashboardCardSkeleton minHeight="min-h-[24rem]" lines={8} />;
  }

  if (isError || (data != null && isPropertyAnalyticsUnavailable(data))) {
    return (
      <DashboardEmptyState
        align="center"
        title="Métricas indisponíveis no momento"
        description="Não foi possível carregar as visualizações deste período. O restante do dashboard continua disponível."
        action={
          <button
            type="button"
            onClick={() => void onRetry()}
            className="rounded-full bg-[#6ea8ff] px-4 py-2 text-xs font-bold text-[#0d1117] transition-opacity hover:opacity-90"
          >
            Tentar novamente
          </button>
        }
        className="min-h-[18rem] justify-center"
      />
    );
  }

  if (!data) {
    return null;
  }

  return (
    <DashboardCard padding={padding} className="flex h-full min-h-0 flex-col">
      <div className="flex min-h-0 w-full flex-1 flex-col gap-4">
        <div className="flex flex-col gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 shrink-0 text-[#6ea8ff]" />
              <h2 className="text-base font-bold text-[#f0f6fc] sm:text-lg">
                Visitas às páginas dos imóveis
              </h2>
            </div>
            <p className="mt-1 text-xs text-[#8b949e] sm:text-sm">
              Visualizações no site 7Cantos por imóvel, com base nos dados de analytics.
            </p>
          </div>

          <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
            <DashboardPeriodFilter
              value={periodPreset}
              onChange={onPeriodChange}
              label="Período"
              compact
              presets={PROPERTY_ANALYTICS_PERIOD_PRESETS}
            />
            <PropertyIdFilter
              properties={propertyOptions}
              selectedPropertyId={propertyId}
              onSelectProperty={onPropertyChange}
            />
          </div>
        </div>

        <PropertyAnalyticsSummary summary={data.summary} />

        <div className={`grid gap-4 ${showRanking ? "xl:grid-cols-2" : "grid-cols-1"}`}>
          <div className="min-w-0">
            <p className="mb-2 text-xs font-semibold text-[#8b949e]">Visualizações por dia</p>
            <PropertyAnalyticsLineChart dailySeries={data.daily_series} />
          </div>

          {showRanking && (
            <div className="min-w-0">
              <PropertyAnalyticsBarChart items={data.items} />
            </div>
          )}
        </div>

        <div className="min-w-0">
          <p className="mb-2 text-xs font-semibold text-[#8b949e]">Detalhamento por imóvel</p>
          <PropertyAnalyticsTable
            key={`${periodPreset}-${propertyId ?? "all"}`}
            items={data.items}
          />
        </div>
      </div>
    </DashboardCard>
  );
}
